import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useMsal } from '@azure/msal-react';
import { jsPDF } from 'jspdf';
// Registers jsPDF.API.svg — the import has no bindings of its own.
import 'svg2pdf.js';
import { incrementSubVersion } from '../../utils/versionUtils';
import { saveDiagram, createDiagram, clearDiagram, getModeler, getViewer } from '../../utils/bpmnModeler';
import { extractImagesFromXml, buildImageRefs, garbageCollectImageStore, hashImageDataUrl, rehydrateSnapshotImages, toJpegDataUrl } from '../../utils/imageProcessing';
import VersionModal from '../VersionModal/VersionModal';
import UnsavedChangesDialog from '../UnsavedChangesDialog/UnsavedChangesDialog';
import NewProcessDialog from '../NewProcessDialog/NewProcessDialog';
import WelcomeDialog from '../WelcomeDialog/WelcomeDialog';
import SaveSuccessDialog from '../SaveSuccessDialog/SaveSuccessDialog';
import RollbackConfirmDialog from '../RollbackConfirmDialog/RollbackConfirmDialog';
import {
  listSharePointBpmnFiles,
  listRecentBpmnFiles,
  loadSharePointBpmnFile,
  saveSharePointBpmnFile,
  convertSgxUpload,
  convertSgxFromSharePoint,
  loadSgxDiagram,
  discardSgxImport,
} from '../../utils/apiClient';
import { detectAndClean } from '../../utils/signavioCleanup';
import { makeEntry, appendEntry, normalizeHistory, parseHistory, readSnapshot, hasSnapshot, sameEntry } from '../../utils/versionHistory';
import { listBausteine } from '../../utils/bausteine';
import './Toolbar.css';

// The SGX endpoint's errors carry a code; only the code is stable. What the
// user gets to read lives here. The size limit is deliberately not repeated —
// it belongs to the server and would drift.
const SGX_ERROR_TEXTS = {
  BPMN_SGX_INVALID: 'Die Datei ist kein lesbares Signavio-Archiv.',
  BPMN_SGX_EMPTY: 'Das Archiv enthält kein Prozessmodell.',
  BPMN_SGX_CONVERT_FAILED: 'Das Archiv konnte nicht konvertiert werden.',
  BPMN_SGX_TOO_LARGE: 'Das Archiv ist zu groß für den Import.',
  BPMN_SGX_IMPORT_NOT_FOUND: 'Der Import ist abgelaufen. Bitte das Archiv erneut laden.',
  BPMN_SGX_DIAGRAM_NOT_FOUND: 'Der Prozess ist nicht mehr verfügbar. Bitte das Archiv erneut laden.',
};

// PDF helpers (module scope to reduce cognitive complexity)

// Raster images embedded in the PDF are re-encoded at this JPEG quality.
// The diagrams themselves go in as vectors and are unaffected.
const PDF_IMAGE_QUALITY = 0.7;

/**
 * Parse an exported SVG string into an element svg2pdf can render, plus the
 * dimensions to lay it out with.
 *
 * The element deliberately stays out of the document: svg2pdf reads styles
 * only from <style>/<link> inside the root it is handed and derives bounding
 * boxes from attributes, so nothing is gained by attaching it — while staying
 * detached keeps the app's CSS out and prevents the bpmn-js marker ids in
 * <defs> from colliding with those of the live canvas.
 */
const parseSvg = (svgString) => {
  const el = new DOMParser().parseFromString(svgString, 'image/svg+xml').documentElement;
  // A parse failure is reported in-band: browsers either make <parsererror>
  // the root or nest it inside one.
  if (!el || el.nodeName === 'parsererror' || el.getElementsByTagName('parsererror').length > 0) {
    throw new Error('Das Diagramm konnte nicht gelesen werden.');
  }
  // saveSVG() writes a viewBox whose origin is the diagram's bounding box —
  // width/height are its third and fourth value, not the origin.
  const viewBox = (el.getAttribute('viewBox') || '').split(/[\s,]+/).map(Number);
  const width = viewBox[2] || parseFloat(el.getAttribute('width'));
  const height = viewBox[3] || parseFloat(el.getAttribute('height'));
  if (!(width > 0) || !(height > 0)) {
    throw new Error('Das Diagramm hat keine verwertbaren Maße.');
  }
  return { el, width, height };
};

/**
 * Re-encode raster images embedded in a diagram as JPEG.
 *
 * svg2pdf passes a data-URL's bytes straight through to jsPDF, so this is the
 * only point at which their compression can be set. Nested SVG images stay
 * untouched — they are vectors already. External references are dropped for
 * the same reason they are in the documentation pages: only data: URLs are
 * ever embedded.
 */
const inlineSvgImagesAsJpeg = async (svgEl) => {
  const XLINK = 'http://www.w3.org/1999/xlink';
  for (const node of Array.from(svgEl.getElementsByTagName('image'))) {
    const href = node.getAttribute('href') || node.getAttributeNS(XLINK, 'href') || '';
    if (/^data:image\/svg\+xml/i.test(href)) continue;
    if (!/^data:image\//i.test(href)) {
      node.remove();
      continue;
    }
    try {
      const jpeg = await toJpegDataUrl(href, PDF_IMAGE_QUALITY);
      if (node.hasAttribute('href')) node.setAttribute('href', jpeg);
      if (node.hasAttributeNS(XLINK, 'href')) node.setAttributeNS(XLINK, 'href', jpeg);
    } catch (e) {
      console.warn('Bild im Diagramm konnte nicht konvertiert werden:', e);
      node.remove();
    }
  }
};

/** Turn an exported SVG string into a page-ready diagram */
const prepareDiagram = async (svgString) => {
  const diagram = parseSvg(svgString);
  await inlineSvgImagesAsJpeg(diagram.el);
  return diagram;
};

/** Extract documentation and image from a business object */
const extractDocInfo = (bo) => {
  const docText = bo.documentation?.length > 0 ? bo.documentation[0].text : null;
  let imageUrl = null;
  try { imageUrl = bo.get('custom:imageUrl'); } catch { /* moddle fallback */ }
  if (!imageUrl) imageUrl = bo.$attrs?.['custom:imageUrl'] ?? null;
  return { docText, imageUrl };
};

/** Add a diagram page (landscape) to the PDF */
const addDiagramPage = async (pdf, diagram, title, subtitle, isFirstPage) => {
  if (!isFirstPage) {
    pdf.addPage('a4', 'landscape');
  }
  const pw = pdf.internal.pageSize.getWidth();
  const ph = pdf.internal.pageSize.getHeight();
  const m = 15;
  const cw = pw - m * 2;

  pdf.setFontSize(isFirstPage ? 18 : 14);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(0);
  pdf.text(title, m, m + 5);
  let headerY = m + 5;

  if (subtitle) {
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text(subtitle, m, m + 12);
    headerY = m + 12;
  }

  const diagramY = headerY + 6;
  const imgAspect = diagram.width / diagram.height;
  let imgW = cw;
  let imgH = imgW / imgAspect;
  const maxImgH = ph - diagramY - m - 3;
  if (imgH > maxImgH) {
    imgH = maxImgH;
    imgW = imgH * imgAspect;
  }
  // loadImages: any href left after inlineSvgImagesAsJpeg is a data: URL —
  // this stops svg2pdf from fetching anything else over the network.
  await pdf.svg(diagram.el, { x: m, y: diagramY, width: imgW, height: imgH, loadImages: /^data:/ });
};

/** Load an image, retrying without CORS on failure */
const loadImageWithFallback = (imageUrl) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => {
      const img2 = new Image();
      img2.onload = () => resolve(img2);
      img2.onerror = reject;
      img2.src = imageUrl;
    };
    img.src = imageUrl;
  });

/** Collect sub-process diagrams as exported SVG */
const collectSubProcessDiagrams = async (canvas, instance, originalRoot) => {
  const diagrams = [];
  const allRoots = canvas.getRootElements ? canvas.getRootElements() : [originalRoot];
  for (const root of allRoots) {
    if (root === originalRoot) continue;
    const bo = root.businessObject;
    if (!bo) continue;
    try {
      canvas.setRootElement(root);
      const { svg } = await instance.saveSVG();
      diagrams.push({ name: bo.name || bo.id, svg });
    } catch (e) {
      console.warn('Could not render sub-process diagram:', bo.name || bo.id, e);
    }
  }
  return diagrams;
};

/**
 * Bausteine als exportierte SVG einsammeln.
 *
 * Sie liegen in eigenen BPMN-Diagrammen und sind über `canvas.getRootElements()`
 * nicht erreichbar — ohne diesen Durchgang fehlten sie im PDF, ohne dass es
 * auffiele. Erreichbar werden sie nur, indem der Betrachter kurz auf ihr
 * Diagramm umgeschaltet wird; am Ende steht wieder das Hauptdiagramm.
 */
const collectBausteinDiagrams = async (instance) => {
  const definitions = instance.getDefinitions?.();
  const bausteine = listBausteine(definitions);
  if (bausteine.length === 0) return [];

  const hauptDiagramm = definitions?.diagrams?.[0]?.id;
  const diagramme = [];
  try {
    for (const baustein of bausteine) {
      if (!baustein.diagram) continue;
      try {
        await instance.open(baustein.diagram.id);
        const canvas = instance.get('canvas');
        const wurzel = canvas.getRootElement();
        const { svg } = await instance.saveSVG();
        diagramme.push({ name: baustein.name, svg });
        // Eingeklappte Sub-Prozesse innerhalb des Bausteins gehören dazu.
        for (const unter of await collectSubProcessDiagrams(canvas, instance, wurzel)) {
          diagramme.push({ name: `${baustein.name} / ${unter.name}`, svg: unter.svg });
        }
      } catch (e) {
        console.warn('Baustein konnte nicht exportiert werden:', baustein.name, e);
      }
    }
  } finally {
    if (hauptDiagramm) {
      try { await instance.open(hauptDiagramm); }
      catch (e) { console.warn('Rückkehr zum Hauptdiagramm fehlgeschlagen:', e); }
    }
  }
  return diagramme;
};

/** Collect task documentation entries from the element registry */
const collectTaskDocs = (elementRegistry) => {
  const docs = [];
  elementRegistry.forEach((element) => {
    const bo = element.businessObject;
    if (!bo?.$instanceOf) return;
    if (!bo.$instanceOf('bpmn:Task') && !bo.$instanceOf('bpmn:SubProcess')) return;
    const { docText, imageUrl } = extractDocInfo(bo);
    if (docText || imageUrl) {
      docs.push({ name: bo.name || bo.id, docText, imageUrl });
    }
  });
  return docs;
};

/** Ensure y position has room, adding a page if needed */
const ensurePageSpace = (pdf, y, needed, m) => {
  if (y > pdf.internal.pageSize.getHeight() - needed) {
    pdf.addPage('a4', 'portrait');
    return m;
  }
  return y;
};

/** Render documentation text for a single task */
const renderTaskDocText = (pdf, docText, m, cw, y) => {
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  const lines = pdf.splitTextToSize(docText, cw);
  for (const line of lines) {
    y = ensurePageSpace(pdf, y, 20, m);
    pdf.text(line, m, y);
    y += 5;
  }
  return y + 3;
};

/** Render a task image in the PDF — only safe data: URLs */
const renderTaskImage = async (pdf, imageUrl, m, cw, y) => {
  // Security: only render data:image/ URLs in PDF, reject external URLs
  if (typeof imageUrl !== 'string' || !/^data:image\//i.test(imageUrl)) {
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'italic');
    pdf.setTextColor(150);
    pdf.text('[Bild nicht verfügbar — externe URL nicht erlaubt]', m, y);
    pdf.setTextColor(0);
    return y + 6;
  }
  try {
    const taskImg = await loadImageWithFallback(imageUrl);
    const data = await toJpegDataUrl(imageUrl, PDF_IMAGE_QUALITY);
    const aspect = taskImg.width / taskImg.height;
    let w = Math.min(cw, 140);
    let h = w / aspect;
    if (h > 90) { h = 90; w = h * aspect; }
    y = ensurePageSpace(pdf, y, h + 15, m);
    pdf.addImage(data, 'JPEG', m, y, w, h);
    return y + h + 5;
  } catch {
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'italic');
    pdf.setTextColor(150);
    pdf.text(`[Bild konnte nicht geladen werden: ${imageUrl}]`, m, y);
    pdf.setTextColor(0);
    return y + 6;
  }
};

/** Add task documentation pages to the PDF */
const addTaskDocPages = async (pdf, taskDocs) => {
  if (taskDocs.length === 0) return;
  pdf.addPage('a4', 'portrait');
  const m = 15;
  const cw = pdf.internal.pageSize.getWidth() - m * 2;
  let y = m;

  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Task-Dokumentation', m, y + 5);
  y += 14;
  pdf.setDrawColor(200);
  pdf.line(m, y, m + cw, y);
  y += 6;

  for (let i = 0; i < taskDocs.length; i++) {
    const task = taskDocs[i];
    y = ensurePageSpace(pdf, y, 50, m);

    pdf.setFontSize(13);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(44, 62, 80);
    pdf.text(`${i + 1}. ${task.name}`, m, y);
    y += 7;
    pdf.setTextColor(0);

    if (task.docText) { y = renderTaskDocText(pdf, task.docText, m, cw, y); }
    if (task.imageUrl) { y = await renderTaskImage(pdf, task.imageUrl, m, cw, y); }

    y += 3;
    if (y < pdf.internal.pageSize.getHeight() - 15) {
      pdf.setDrawColor(220);
      pdf.line(m, y, m + cw, y);
      y += 6;
    }
  }
};

const Toolbar = ({ modeler, mode, onModeChange, onToggleVideo, videoPlayerOpen, hasVideoUrl, onModelerReady, isDirty, setIsDirty, processName, setProcessName, directoryHandleRef, authorName, isAuthenticated, onLogout, helpPanelOpen, onToggleHelp, gridVisible, onToggleGrid, viewingVersion, onViewVersion, onExitVersionView }) => {
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [versionDisplay, setVersionDisplay] = useState('1.0');
  const [pendingAction, setPendingAction] = useState(null); // null | 'load' | 'new'
  const [showNewProcessDialog, setShowNewProcessDialog] = useState(false);
  const [showWelcomeDialog, setShowWelcomeDialog] = useState(true);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [signavioImportReport, setSignavioImportReport] = useState(null);
  const { instance: msalInstance, accounts } = useMsal();
  const [showSaveDropdown, setShowSaveDropdown] = useState(false);
  const [showSaveAsDialog, setShowSaveAsDialog] = useState(false);
  const [selectedCount, setSelectedCount] = useState(0);
  const [showAlignDropdown, setShowAlignDropdown] = useState(false);
  const [showRollbackConfirm, setShowRollbackConfirm] = useState(false);
  const [rollbackTarget, setRollbackTarget] = useState(null);
  const [rollbackVersionsToDelete, setRollbackVersionsToDelete] = useState([]);
  const [showLoadSourceDialog, setShowLoadSourceDialog] = useState(false);
  const [loadSourceContext, setLoadSourceContext] = useState('toolbar');
  const [showSaveTargetDialog, setShowSaveTargetDialog] = useState(false);
  const [saveTargetContext, setSaveTargetContext] = useState('saveAs');
  const [pendingSaveName, setPendingSaveName] = useState('');
  const [showSharePointDialog, setShowSharePointDialog] = useState(false);
  const [sharePointDialogMode, setSharePointDialogMode] = useState('load');
  const [sharePointSaveFileName, setSharePointSaveFileName] = useState('');
  const [sharePointFiles, setSharePointFiles] = useState([]);
  const [sharePointRecentFiles, setSharePointRecentFiles] = useState([]);
  const [sharePointShowAllRecent, setSharePointShowAllRecent] = useState(false);
  const [sharePointLoading, setSharePointLoading] = useState(false);
  const [sharePointError, setSharePointError] = useState('');
  const [sharePointBrowsePath, setSharePointBrowsePath] = useState('Kundenprojekte');
  // SGX import: a Signavio archive normally carries several processes (a
  // workspace export carries hundreds). The server converts once and keeps the
  // result; what lives here is the catalogue and the choice made on it.
  const [sgxBusy, setSgxBusy] = useState('');
  const [sgxError, setSgxError] = useState('');
  // Where the failed import came from — the welcome dialog may only come back
  // once the error has been dismissed, otherwise both overlays stack.
  const [sgxErrorContext, setSgxErrorContext] = useState('toolbar');
  const [showSgxPicker, setShowSgxPicker] = useState(false);
  const [sgxImport, setSgxImport] = useState(null); // { importId, sourceName, diagrams }
  const [sgxFilter, setSgxFilter] = useState('');
  const saveDropdownRef = useRef(null);
  const alignDropdownRef = useRef(null);
  const pendingFileContentRef = useRef(null);
  const pendingFileNameRef = useRef(null);
  const pendingFilePathRef = useRef(null);
  const pendingFileEtagRef = useRef(null);
  // A diagram converted from SGX has no file behind it yet: it must count as
  // unsaved, otherwise the next load silently discards it.
  const pendingFileFreshRef = useRef(false);
  const currentSharePointPathRef = useRef('');
  const currentSharePointEtagRef = useRef('');
  const baselineXmlRef = useRef('');
  // Set to true when an import auto-cleaned the XML (e.g. Signavio export).
  // The first subsequent save then skips the version-history XML snapshot,
  // so the file does not carry a 1.3 MB self-copy on disk. Reset after use.
  const freshImportRef = useRef(false);

  // Close save dropdown on outside click
  const handleClickOutside = useCallback((e) => {
    if (saveDropdownRef.current && !saveDropdownRef.current.contains(e.target)) {
      setShowSaveDropdown(false);
    }
  }, []);

  useEffect(() => {
    if (showSaveDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showSaveDropdown, handleClickOutside]);

  // Close alignment dropdown on outside click
  const handleAlignClickOutside = useCallback((e) => {
    if (alignDropdownRef.current && !alignDropdownRef.current.contains(e.target)) {
      setShowAlignDropdown(false);
    }
  }, []);

  useEffect(() => {
    if (showAlignDropdown) {
      document.addEventListener('mousedown', handleAlignClickOutside);
      return () => document.removeEventListener('mousedown', handleAlignClickOutside);
    }
  }, [showAlignDropdown, handleAlignClickOutside]);

  // Track selection count for alignment buttons
  useEffect(() => {
    const activeModeler = getActiveModeler();
    if (!activeModeler) return;
    try {
      const eventBus = activeModeler.get('eventBus');
      const onSelectionChanged = (e) => {
        setSelectedCount(e.newSelection ? e.newSelection.length : 0);
      };
      eventBus.on('selection.changed', onSelectionChanged);
      return () => eventBus.off('selection.changed', onSelectionChanged);
    } catch { /* modeler not ready */ }
  }, [modeler]);
  
  // Use the modeler from props or fallback to getModeler()
  const getActiveModeler = () => modeler || getModeler();

  const normalizeSharePointPath = (rawPath) => {
    const p = String(rawPath || '').replace(/\\/g, '/').trim();
    if (!p) return 'Kundenprojekte';
    const idx = p.toLowerCase().indexOf('/kundenprojekte');
    let normalized = idx >= 0 ? p.slice(idx + 1) : p.replace(/^\/+/, '');
    if (!/^kundenprojekte(\/|$)/i.test(normalized)) {
      normalized = `Kundenprojekte/${normalized}`;
    }
    return normalized.replace(/\/+/g, '/').replace(/\/$/, '');
  };

  const ensureBpmnFileName = (rawName) => {
    const cleaned = String(rawName || '').trim().replace(/[\\/:*?"<>|]+/g, '_');
    if (!cleaned) return 'prozess.bpmn';
    if (/\.(bpmn|xml)$/i.test(cleaned)) return cleaned;
    return `${cleaned}.bpmn`;
  };

  const isSgxFileName = (name) => /\.sgx$/i.test(String(name || ''));

  const extractApiErrorCode = (err) => {
    const msg = String(err?.message || '');
    const match = msg.match(/^([A-Z0-9_]+):/);
    return match ? match[1] : '';
  };

  const isViewMode = mode === 'view';

  const displayedSharePointFiles = useMemo(() => {
    const all = Array.isArray(sharePointFiles) ? sharePointFiles.slice() : [];
    // When saving, an archive is not a possible target — the editor writes
    // BPMN, never SGX.
    const files = sharePointDialogMode === 'save'
      ? all.filter((f) => f?.isFolder || !/\.sgx$/i.test(String(f?.name || '')))
      : all;
    return files.sort((a, b) => {
      const aIsFolder = !!a?.isFolder;
      const bIsFolder = !!b?.isFolder;
      if (aIsFolder !== bIsFolder) return aIsFolder ? -1 : 1;
      const ta = Date.parse(a?.lastModifiedDateTime || '') || 0;
      const tb = Date.parse(b?.lastModifiedDateTime || '') || 0;
      if (tb !== ta) return tb - ta;
      return String(a?.name || '').localeCompare(String(b?.name || ''), 'de');
    });
  }, [sharePointFiles, sharePointDialogMode]);

  const displayedSgxDiagrams = useMemo(() => {
    const diagrams = Array.isArray(sgxImport?.diagrams) ? sgxImport.diagrams : [];
    const needle = sgxFilter.trim().toLowerCase();
    const matching = needle
      ? diagrams.filter((d) =>
        `${d.customer || ''} ${d.name || ''}`.toLowerCase().includes(needle))
      : diagrams;
    return matching.slice().sort((a, b) => {
      const byCustomer = String(a.customer || '').localeCompare(String(b.customer || ''), 'de');
      if (byCustomer !== 0) return byCustomer;
      return String(a.name || '').localeCompare(String(b.name || ''), 'de');
    });
  }, [sgxImport, sgxFilter]);

  const displayedRecentFiles = useMemo(() => {
    if (sharePointShowAllRecent) return sharePointRecentFiles;
    return sharePointRecentFiles.slice(0, 5);
  }, [sharePointRecentFiles, sharePointShowAllRecent]);

  const sharePointBreadcrumbs = useMemo(() => {
    const normalized = normalizeSharePointPath(sharePointBrowsePath || 'Kundenprojekte');
    const parts = normalized.split('/').filter(Boolean);
    const crumbs = [];
    let acc = '';
    for (const part of parts) {
      acc = acc ? `${acc}/${part}` : part;
      crumbs.push({ label: part, path: acc });
    }
    return crumbs;
  }, [sharePointBrowsePath]);

  const getParentSharePointPath = (rawPath) => {
    const normalized = normalizeSharePointPath(rawPath || 'Kundenprojekte');
    if (/^kundenprojekte$/i.test(normalized)) return normalized;
    const parts = normalized.split('/').filter(Boolean);
    if (parts.length <= 1) return 'Kundenprojekte';
    return parts.slice(0, -1).join('/');
  };

  const getSharePointDirectoryPath = (rawPath) => {
    const normalized = normalizeSharePointPath(rawPath || 'Kundenprojekte');
    const parts = normalized.split('/').filter(Boolean);
    if (parts.length <= 1) return normalized;
    return parts.slice(0, -1).join('/');
  };

  const formatSharePointMeta = (file) => {
    if (file?.isFolder) return 'Ordner';
    const parts = [];
    const modified = Date.parse(file?.lastModifiedDateTime || '');
    if (modified) {
      parts.push(new Date(modified).toLocaleString('de-DE'));
    }
    if (typeof file?.size === 'number' && file.size >= 0) {
      const kb = file.size / 1024;
      if (kb < 1024) {
        parts.push(`${Math.max(1, Math.round(kb))} KB`);
      } else {
        parts.push(`${(kb / 1024).toFixed(1)} MB`);
      }
    }
    return parts.join(' · ');
  };

  // Get the active instance (modeler or viewer)
  const getActiveInstance = () => {
    const m = getActiveModeler();
    if (m) return m;
    return getViewer();
  };

  // Robust reader for custom moddle attributes (with $attrs fallback)
  const readCustomAttr = (definitions, name) => {
    let val;
    try { val = definitions.get('custom:' + name); } catch { /* moddle may not support get */ }
    if (val === undefined || val === null) {
      val = definitions.$attrs?.['custom:' + name];
    }
    if (val === undefined || val === null) {
      val = definitions[name];
    }
    return val;
  };

  // Read version info from definitions
  const getVersionInfo = () => {
    const instance = getActiveInstance();
    if (!instance?.getDefinitions) return { version: '1.0', author: authorName || 'Unbekannt', history: [] };
    try {
      const definitions = instance.getDefinitions();
      if (!definitions) return { version: '1.0', author: authorName || 'Unbekannt', history: [] };
      const version = readCustomAttr(definitions, 'version') || '1.0';
      const author = readCustomAttr(definitions, 'author') || authorName || 'Unbekannt';
      let history = [];
      try {
        history = JSON.parse(readCustomAttr(definitions, 'versionHistory') || '[]');
      } catch { /* invalid JSON */ }
      return { version, author, history };
    } catch {
      return { version: '1.0', author: authorName || 'Unbekannt', history: [] };
    }
  };

  const handleOpenVersionModal = () => {
    const info = getVersionInfo();
    setVersionDisplay(info.version);
    setShowVersionModal(true);
  };

  // --- Version snapshot helpers (extracted to reduce cognitive complexity) ---

  /** Capture the current XML and strip recursive attributes (versionHistory, imageStore). */
  const captureSnapshot = async (instance) => {
    const { xml } = await instance.saveXML({ format: true });
    return xml.replaceAll(/\s*custom:versionHistory="[^"]*"/g, '')
              .replaceAll(/\s*custom:versionHistory='[^']*'/g, '')
              .replaceAll(/\s*custom:imageStore="[^"]*"/g, '')
              .replaceAll(/\s*custom:imageStore='[^']*'/g, '');
  };

  /** Update the baseline snapshot (clean state for rollback). */
  const updateBaseline = async (instance) => {
    try { baselineXmlRef.current = await captureSnapshot(instance); }
    catch (e) { console.warn('Could not update baseline:', e); }
  };

  /** Extract images from snapshot XML and build hash-based references for deduplication. */
  const processSnapshotImages = async (snapshotXml, existingStore) => {
    const { images, xml: strippedXml } = extractImagesFromXml(snapshotXml);
    let imageRefs = {};
    if (Object.keys(images).length > 0) {
      const { refs, newImages } = await buildImageRefs(images, existingStore);
      imageRefs = refs;
      Object.assign(existingStore, newImages);
    }
    return { strippedXml, imageRefs };
  };

  /** Collect SHA-256 hashes of all images currently on diagram elements. */
  const collectCurrentImageHashes = async (instance, store) => {
    const hashes = [];
    const elementRegistry = instance.get('elementRegistry');
    for (const el of elementRegistry.getAll()) {
      const bo = el.businessObject;
      let imgUrl;
      try { imgUrl = bo?.get?.('custom:imageUrl'); } catch { /* */ }
      if (!imgUrl) imgUrl = bo?.$attrs?.['custom:imageUrl'];
      if (imgUrl) {
        const h = await hashImageDataUrl(imgUrl);
        hashes.push(h);
        if (!store[h]) store[h] = imgUrl;
      }
    }
    return hashes;
  };

  /** Persist imageStore and versionHistory to definitions attributes. */
  const persistStoreAndHistory = (definitions, store, history) => {
    const storeJson = JSON.stringify(store);
    definitions.set('custom:imageStore', storeJson);
    if (!definitions.$attrs) definitions.$attrs = {};
    definitions.$attrs['custom:imageStore'] = storeJson;
    // Umpacken und Kappen sitzen hier, damit jeder Schreibweg sie durchläuft.
    const historyJson = JSON.stringify(normalizeHistory(history));
    definitions.set('custom:versionHistory', historyJson);
    definitions.$attrs['custom:versionHistory'] = historyJson;
  };

  const handleNewVersion = async (newVersion, authorName, comment) => {
    const instance = getActiveInstance();
    if (!instance?.getDefinitions) return;
    try {
      const definitions = instance.getDefinitions();
      if (!definitions) return;
      const today = new Date().toISOString().split('T')[0];

      // Weiterschreiben auf einer unlesbaren Historie überschreibt sie und
      // vernichtet sie damit endgültig. Lieber gar keine Version anlegen.
      const parsedHistory = parseHistory(readCustomAttr(definitions, 'versionHistory'));
      if (parsedHistory.corrupt) {
        console.error('Versionshistorie ist unlesbar — keine neue Version angelegt.');
        alert('Die Versionshistorie dieser Datei ist beschädigt. Es wurde keine neue Version angelegt, '
          + 'damit die vorhandene Historie nicht überschrieben wird.');
        return;
      }

      // 1. Read current version info BEFORE bumping
      const oldVersion = readCustomAttr(definitions, 'version') || '1.0';
      const oldAuthor = readCustomAttr(definitions, 'author') || authorName || 'Unbekannt';

      // 2. Use baseline snapshot (= state before user changes) for old version entry
      //    Fall back to live capture if no baseline exists (first version)
      let snapshotXml = baselineXmlRef.current || '';
      if (!snapshotXml) {
        try { snapshotXml = await captureSnapshot(instance); }
        catch (e) { console.warn('Could not capture XML snapshot:', e); }
      }

      // 3. Extract images and build hash references
      let existingStore = {};
      try { existingStore = JSON.parse(readCustomAttr(definitions, 'imageStore') || '{}'); }
      catch { /* invalid JSON */ }

      let imageRefs = {};
      if (snapshotXml) {
        try {
          const result = await processSnapshotImages(snapshotXml, existingStore);
          snapshotXml = result.strippedXml;
          imageRefs = result.imageRefs;
        } catch (e) { console.warn('Image processing failed, saving snapshot without image dedup:', e); }
      }

      // 4. Build history entry
      let history = parsedHistory.history;
      const historyEntry = makeEntry({
        version: oldVersion, author: oldAuthor, date: today, comment,
        xml: snapshotXml, images: imageRefs,
      });
      // Eine ausdrücklich angelegte Version wird immer vermerkt, auch wenn
      // sich am Diagramm nichts geändert hat — sie ist eine Aussage des
      // Nutzers, kein Nebenprodukt des Speicherns.
      history.push(historyEntry);
      // Vor dem Aufraeumen kappen, sonst gelten die Bilder gerade
      // entfernter Staende noch eine Runde lang als belegt.
      history = normalizeHistory(history);

      // 5. Garbage-collect image store
      let currentImageHashes = [];
      try { currentImageHashes = await collectCurrentImageHashes(instance, existingStore); }
      catch { /* elementRegistry not available */ }
      const cleanedStore = garbageCollectImageStore(existingStore, currentImageHashes, history);

      // 6. Persist and bump version
      persistStoreAndHistory(definitions, cleanedStore, history);
      definitions.set('custom:version', newVersion);
      definitions.set('custom:author', authorName);
      definitions.set('custom:lastModified', today);
      setVersionDisplay(newVersion);

      // Update baseline to current state (= the new version's clean state)
      await updateBaseline(instance);
    } catch (error_) {
      console.error('Error setting version:', error_);
    }
  };
  
  // Helper: write version history entry (with XML snapshot) to definitions before save
  const writeVersionHistory = async () => {
    const instance = getActiveInstance();
    if (!instance?.getDefinitions) return;
    const definitions = instance.getDefinitions();
    if (!definitions) return;
    const version = readCustomAttr(definitions, 'version') || '1.0';
    const author = readCustomAttr(definitions, 'author') || authorName || 'Unbekannt';
    const today = new Date().toISOString().split('T')[0];

    // Zuerst prüfen, ob die vorhandene Historie überhaupt lesbar ist — sonst
    // wäre das Aufnehmen des Schnappschusses vergebene Arbeit. Ist sie es
    // nicht, bleiben Historie und Bildspeicher unangetastet: Das Diagramm
    // wird gespeichert, die beschädigte Historie bleibt zur Rettung stehen.
    const parsed = parseHistory(readCustomAttr(definitions, 'versionHistory'));
    if (parsed.corrupt) {
      console.warn('Versionshistorie ist unlesbar — dieser Speichervorgang schreibt keinen Eintrag.');
      definitions.set('custom:lastModified', today);
      if (!definitions.$attrs) definitions.$attrs = {};
      definitions.$attrs['custom:lastModified'] = today;
      if (freshImportRef.current) freshImportRef.current = false;
      await updateBaseline(instance);
      return;
    }

    // Use baseline snapshot for rollback support (= state at last load/save/version)
    // Fall back to live capture if no baseline exists.
    // After a fresh import (e.g. Signavio), skip the snapshot entirely so the
    // saved file does not double in size from an embedded self-copy.
    const skipSnapshot = freshImportRef.current;
    let snapshotXml = '';
    if (!skipSnapshot) {
      snapshotXml = baselineXmlRef.current || '';
      if (!snapshotXml) {
        try { snapshotXml = await captureSnapshot(instance); }
        catch (e) { console.warn('Could not capture XML snapshot on save:', e); }
      }
    }

    // Extract images and build hash references
    let existingStore = {};
    try { existingStore = JSON.parse(readCustomAttr(definitions, 'imageStore') || '{}'); }
    catch { /* invalid JSON */ }

    let imageRefs = {};
    if (snapshotXml) {
      try {
        const result = await processSnapshotImages(snapshotXml, existingStore);
        snapshotXml = result.strippedXml;
        imageRefs = result.imageRefs;
      } catch (e) { console.warn('Image processing failed, saving snapshot without image dedup:', e); }
    }

    // Build history entry
    let history = parsed.history;
    const historyEntry = makeEntry({
      version, author, date: today, xml: snapshotXml, images: imageRefs,
    });
    // Speichern ohne Änderung legte bisher eine vollständige Zweitkopie an.
    const appended = appendEntry(history, historyEntry);
    history = normalizeHistory(appended.history);

    // Garbage-collect image store
    let currentImageHashes = [];
    try { currentImageHashes = await collectCurrentImageHashes(instance, existingStore); }
    catch { /* elementRegistry not available */ }
    const cleanedStore = garbageCollectImageStore(existingStore, currentImageHashes, history);

    // Persist
    persistStoreAndHistory(definitions, cleanedStore, history);
    definitions.set('custom:lastModified', today);
    if (!definitions.$attrs) definitions.$attrs = {};
    definitions.$attrs['custom:lastModified'] = today;

    // Reset the fresh-import flag — subsequent saves include the snapshot again.
    if (freshImportRef.current) freshImportRef.current = false;

    // Update baseline to current state after save
    await updateBaseline(instance);
  };

  // Helper: save via File System Access API (Chrome/Edge)
  const saveViaFileSystem = async (xml, fileName) => {
    const fileTypeOpts = {
      suggestedName: fileName,
      types: [{ description: 'BPMN Dateien', accept: { 'application/xml': ['.bpmn', '.xml'] } }]
    };

    if (!directoryHandleRef.current) {
      directoryHandleRef.current = await globalThis.showSaveFilePicker(fileTypeOpts);
    }

    let fileHandle = directoryHandleRef.current;
    try {
      const writable = await fileHandle.createWritable();
      await writable.write(xml);
      await writable.close();
    } catch {
      // Permission revoked or handle invalid — ask again
      fileHandle = await globalThis.showSaveFilePicker(fileTypeOpts);
      directoryHandleRef.current = fileHandle;
      const writable = await fileHandle.createWritable();
      await writable.write(xml);
      await writable.close();
    }
  };

  // Helper: save via classic blob download (Firefox, etc.)
  const saveViaDownload = (xml, fileName) => {
    const blob = new Blob([xml], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
  };

  const pickSharePointFileAndRead = async (rawPath) => {
    const path = normalizeSharePointPath(rawPath);
    const account = accounts && accounts[0];
    const data = await loadSharePointBpmnFile({
      msalInstance,
      account,
      path,
    });
    return {
      content: data.content,
      fileName: data.name || path.split('/').pop() || 'prozess.bpmn',
      path: normalizeSharePointPath(data.path || path),
      etag: data.etag || null,
    };
  };

  const refreshSharePointFiles = async (folderPath = sharePointBrowsePath, retryAttempt = 0) => {
    const account = accounts && accounts[0];
    const targetPath = normalizeSharePointPath(folderPath || 'Kundenprojekte');
    setSharePointLoading(true);
    setSharePointError('');
    try {
      const files = await listSharePointBpmnFiles({
        msalInstance,
        account,
        path: targetPath,
        limit: 200,
      });
      const entries = files.filter((f) => {
        if (f?.isFolder) return true;
        // .sgx belongs here: it is converted on open, so to the user it is
        // just as loadable a process source as a .bpmn.
        return /\.(bpmn|xml|sgx)$/i.test(String(f?.name || ''));
      });
      setSharePointBrowsePath(targetPath);
      setSharePointFiles(entries);
      return entries;
    } catch (err) {
      const message = err?.message || '';
      const isSessionIssue =
        message.includes('Keine angemeldete Sitzung') ||
        message.includes('Anmeldung wird neu gestartet');

      // MSAL state can be briefly empty right after redirect/popup completion.
      // Retry once before surfacing an error in the dialog.
      if (isSessionIssue && retryAttempt < 1) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        return refreshSharePointFiles(targetPath, retryAttempt + 1);
      }

      setSharePointError(message || 'SharePoint-Dateien konnten nicht geladen werden.');
      setSharePointFiles([]);
      return [];
    } finally {
      setSharePointLoading(false);
    }
  };

  const refreshSharePointRecentFiles = async () => {
    const account = accounts && accounts[0];
    try {
      const recent = await listRecentBpmnFiles({
        msalInstance,
        account,
        limit: 10,
      });
      const entries = recent.filter((f) => /\.(bpmn|xml)$/i.test(String(f?.name || f?.path || '')));
      setSharePointRecentFiles(entries);
      return entries;
    } catch {
      setSharePointRecentFiles([]);
      return [];
    }
  };

  const startSharePointDialog = async (context, options = {}) => {
    const mode = options.mode === 'save' ? 'save' : 'load';
    const initialBrowsePath = currentSharePointPathRef.current
      ? getSharePointDirectoryPath(currentSharePointPathRef.current)
      : 'Kundenprojekte';
    setLoadSourceContext(context);
    setSharePointDialogMode(mode);
    setSharePointSaveFileName(ensureBpmnFileName(options.fileName || `${processName || 'prozess'}.bpmn`));
    setShowSharePointDialog(true);
    setSharePointShowAllRecent(false);
    setSharePointBrowsePath(initialBrowsePath);
    if (mode === 'load') {
      await refreshSharePointRecentFiles();
    }
    await refreshSharePointFiles(initialBrowsePath);
  };

  const startSaveTargetDialog = (context, name) => {
    setSaveTargetContext(context);
    setPendingSaveName(name || processName || 'prozess');
    setShowSaveTargetDialog(true);
  };

  // --- Signavio archives (.sgx) --------------------------------------------
  // The editor loads BPMN. A .sgx is an archive full of Signavio models and is
  // therefore converted server-side before it can be opened.

  const discardCurrentSgxImport = (importId) => {
    if (!importId) return;
    discardSgxImport({ msalInstance, account: accounts && accounts[0], importId });
  };

  const closeSgxPicker = () => {
    const importId = sgxImport?.importId;
    setShowSgxPicker(false);
    setSgxFilter('');
    setSgxImport(null);
    discardCurrentSgxImport(importId);
  };

  const openSgxDiagram = async (catalog, entry, context) => {
    const account = accounts && accounts[0];
    const diagram = await loadSgxDiagram({
      msalInstance,
      account,
      importId: catalog.importId,
      diagramId: entry.id,
    });
    // The conversion result has no file behind it: no SharePoint path, no
    // etag, and marked unsaved.
    await continueWithLoadedResult(
      { content: diagram.content, fileName: diagram.fileName || `${entry.name}.bpmn`, fresh: true },
      context,
    );
  };

  /**
   * Convert an archive and open it. `convert` yields the catalogue; if it holds
   * exactly one process that one is loaded, otherwise the user picks.
   */
  const describeSgxError = (err) => {
    // Die Ratengrenze ist geteilte Infrastruktur und meldet sich englisch ohne
    // Code — hier bekommt sie denselben Ton wie die übrigen Meldungen.
    if (err?.status === 429) {
      return 'Zu viele Importe in kurzer Zeit. Bitte einen Moment warten.';
    }
    const code = extractApiErrorCode(err);
    return SGX_ERROR_TEXTS[code] || err?.message || 'SGX-Import fehlgeschlagen.';
  };

  const runSgxImport = async (convert, sourceName, context) => {
    setSgxError('');
    setSgxBusy(`${sourceName} wird konvertiert …`);
    try {
      const catalog = await convert();
      const diagrams = Array.isArray(catalog?.diagrams) ? catalog.diagrams : [];
      if (diagrams.length === 0) {
        throw new Error(SGX_ERROR_TEXTS.BPMN_SGX_EMPTY);
      }
      if (diagrams.length === 1) {
        // Freigeben auch dann, wenn das Laden scheitert — sonst bliebe das
        // konvertierte Archiv bis zum Ablauf auf dem Server liegen.
        try {
          await openSgxDiagram(catalog, diagrams[0], context);
        } finally {
          discardCurrentSgxImport(catalog.importId);
        }
        return;
      }
      setSgxImport({ ...catalog, context });
      setSgxFilter('');
      setShowSgxPicker(true);
    } catch (err) {
      setSgxErrorContext(context);
      setSgxError(describeSgxError(err));
    } finally {
      setSgxBusy('');
    }
  };

  const handleSgxErrorDismiss = () => {
    const context = sgxErrorContext;
    setSgxError('');
    if (context === 'welcome') {
      setShowWelcomeDialog(true);
    }
  };

  const handleSgxPick = async (entry) => {
    const catalog = sgxImport;
    if (!catalog) return;
    setSgxError('');
    setSgxBusy('Prozess wird geöffnet …');
    try {
      await openSgxDiagram(catalog, entry, catalog.context);
      closeSgxPicker();
    } catch (err) {
      setSgxError(describeSgxError(err));
    } finally {
      setSgxBusy('');
    }
  };

  const handleSgxPickerCancel = () => {
    const context = sgxImport?.context;
    closeSgxPicker();
    if (context === 'welcome') {
      setShowWelcomeDialog(true);
    }
  };

  const continueWithLoadedResult = async (result, context) => {
    if (!result) return;
    const meta = { path: result.path || null, etag: result.etag || null };
    const options = { fresh: !!result.fresh };
    if (context === 'welcome') {
      await executeLoad(result.content, result.fileName, meta, options);
      return;
    }

    if (isDirty) {
      pendingFileContentRef.current = result.content;
      pendingFileNameRef.current = result.fileName;
      pendingFilePathRef.current = result.path || null;
      pendingFileEtagRef.current = result.etag || null;
      pendingFileFreshRef.current = !!result.fresh;
      setPendingAction('load');
    } else {
      await executeLoad(result.content, result.fileName, meta, options);
    }
  };

  const handleSharePointLoad = async (rawPath, retryAttempt = 0) => {
    if (!rawPath) return;
    if (isSgxFileName(rawPath)) {
      const context = loadSourceContext;
      const path = normalizeSharePointPath(rawPath);
      const account = accounts && accounts[0];
      setShowSharePointDialog(false);
      await runSgxImport(
        () => convertSgxFromSharePoint({ msalInstance, account, path }),
        path.split('/').pop() || 'Archiv',
        context,
      );
      return;
    }
    setSharePointLoading(true);
    setSharePointError('');
    try {
      const result = await pickSharePointFileAndRead(rawPath);
      await refreshSharePointRecentFiles();
      setShowSharePointDialog(false);
      await continueWithLoadedResult(result, loadSourceContext);
    } catch (err) {
      const message = err?.message || '';
      const isSessionIssue =
        message.includes('Keine angemeldete Sitzung') ||
        message.includes('Anmeldung wird neu gestartet');

      if (isSessionIssue && retryAttempt < 1) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        return handleSharePointLoad(rawPath, retryAttempt + 1);
      }

      setSharePointError(message || 'SharePoint-Laden fehlgeschlagen.');
    } finally {
      setSharePointLoading(false);
    }
  };

  const handleSharePointDialogCancel = () => {
    setShowSharePointDialog(false);
    if (loadSourceContext === 'welcome') {
      setShowWelcomeDialog(true);
    }
  };

  const handleLoadSourceCancel = () => {
    setShowLoadSourceDialog(false);
    if (loadSourceContext === 'welcome') {
      setShowWelcomeDialog(true);
    }
  };

  const handleLoadSourceLocal = async () => {
    const context = loadSourceContext;
    setShowLoadSourceDialog(false);
    const result = await pickFileAndRead();
    if (!result) {
      if (context === 'welcome') {
        setShowWelcomeDialog(true);
      }
      return;
    }
    if (result.sgxFile) {
      const account = accounts && accounts[0];
      // Ein Workspace-Export bringt zweistellige Megabyte mit; ohne Anzeige
      // steht der Nutzer minutenlang vor einem stummen Dialog.
      const onProgress = (loaded, total) => {
        if (!total) return;
        const anteil = Math.round((loaded / total) * 100);
        setSgxBusy(anteil < 100
          ? `${result.fileName} wird übertragen … ${anteil} %`
          : `${result.fileName} wird konvertiert …`);
      };
      await runSgxImport(
        () => convertSgxUpload(result.sgxFile, { msalInstance, account, onProgress }),
        result.fileName,
        context,
      );
      return;
    }
    await continueWithLoadedResult(result, context);
  };

  const handleLoadSourceSharePoint = async () => {
    const context = loadSourceContext;
    setShowLoadSourceDialog(false);
    await startSharePointDialog(context, { mode: 'load' });
  };

  const handleSaveTargetCancel = () => {
    setShowSaveTargetDialog(false);
    setPendingSaveName('');
  };

  const handleSaveTargetLocal = async () => {
    setShowSaveTargetDialog(false);
    currentSharePointPathRef.current = '';
    currentSharePointEtagRef.current = '';
    await performSave({ preferLocal: true });
  };

  const handleSaveTargetSharePoint = async () => {
    const context = saveTargetContext;
    const fileName = ensureBpmnFileName(`${pendingSaveName || processName || 'prozess'}`);
    setShowSaveTargetDialog(false);
    await startSharePointDialog(context, { mode: 'save', fileName });
  };

  const handleSharePointNavigateTo = async (targetPath) => {
    await refreshSharePointFiles(targetPath);
  };

  const handleSharePointNavigateUp = async () => {
    const parentPath = getParentSharePointPath(sharePointBrowsePath);
    await handleSharePointNavigateTo(parentPath);
  };

  const handleSharePointRecentFilePick = async (file) => {
    if (sharePointDialogMode === 'save') {
      if (file?.path) {
        const dir = getSharePointDirectoryPath(file.path);
        await handleSharePointNavigateTo(dir);
      }
      setSharePointSaveFileName(ensureBpmnFileName(file?.name || sharePointSaveFileName));
      return;
    }
    await handleSharePointLoad(file?.path || file?.name || '');
  };

  const handleSharePointFilePick = async (file) => {
    if (file?.isFolder) {
      const folderPath = normalizeSharePointPath(file.path || `${sharePointBrowsePath}/${file.name}`);
      await handleSharePointNavigateTo(folderPath);
      return;
    }
    if (sharePointDialogMode === 'save') {
      setSharePointSaveFileName(ensureBpmnFileName(file?.name || sharePointSaveFileName));
      return;
    }
    await handleSharePointLoad(file?.path || file?.name || '');
  };

  const handleSharePointSaveConfirm = async () => {
    const fileName = ensureBpmnFileName(sharePointSaveFileName);
    if (!fileName) {
      setSharePointError('Bitte Dateinamen angeben.');
      return;
    }
    const folderPath = normalizeSharePointPath(sharePointBrowsePath || 'Kundenprojekte');
    currentSharePointPathRef.current = normalizeSharePointPath(`${folderPath}/${fileName}`);
    currentSharePointEtagRef.current = '';
    setSharePointError('');
    setShowSharePointDialog(false);
    await performSave();
  };

  // The actual write-to-disk save flow (no validation).
  const performSave = async (options = {}) => {
    const preferLocal = !!options.preferLocal;
    try {
      await writeVersionHistory();
      const xml = await saveDiagram();
      const fileName = processName + '.bpmn';
      const account = accounts && accounts[0];
      const targetPath = normalizeSharePointPath(
        currentSharePointPathRef.current || `Kundenprojekte/${fileName}`,
      );

      if (!preferLocal) {
        try {
          const saved = await saveSharePointBpmnFile({
            msalInstance,
            account,
            path: targetPath,
            content: xml,
            ifMatch: currentSharePointEtagRef.current || null,
          });
          currentSharePointPathRef.current = normalizeSharePointPath(saved?.path || targetPath);
          currentSharePointEtagRef.current = saved?.etag || '';
          if (setIsDirty) setIsDirty(false);
          setShowSaveSuccess(true);
          return;
        } catch (spErr) {
          const code = extractApiErrorCode(spErr);
          if (code === 'BPMN_ETAG_CONFLICT') {
            alert('Speichern abgebrochen: Die Datei wurde in SharePoint zwischenzeitlich geändert. Bitte neu laden und erneut speichern.');
            return;
          }
          if (code === 'BPMN_SHAREPOINT_FORBIDDEN' || code === 'BPMN_AUTH_REQUIRED') {
            alert('Speichern in SharePoint nicht möglich: Berechtigung oder Anmeldung fehlt.');
            return;
          }
          console.warn('SharePoint-Save fehlgeschlagen, Fallback lokal:', spErr);
        }
      }

      if (globalThis.showSaveFilePicker) {
        try {
          await saveViaFileSystem(xml, fileName);
          if (setIsDirty) setIsDirty(false);
          setShowSaveSuccess(true);
          return;
        } catch (error_) {
          if (error_.name === 'AbortError') return;
          console.warn('File System Access API failed, falling back to download:', error_);
        }
      }

      saveViaDownload(xml, fileName);
      if (setIsDirty) setIsDirty(false);
      setShowSaveSuccess(true);
    } catch (err) {
      console.error('Error saving diagram:', err);
      alert('Fehler beim Speichern: ' + err.message);
    }
  };

  const handleSave = async () => {
    if (!processName) {
      alert('Kein Prozessname gesetzt. Bitte zuerst einen neuen Prozess erstellen oder laden.');
      return;
    }

    await performSave();
  };

  const handleSaveAs = (newName) => {
    setShowSaveAsDialog(false);
    if (!newName) return;

    // Update process name in definitions and process element
    const instance = getActiveInstance();
    if (instance?.getDefinitions) {
      try {
        const definitions = instance.getDefinitions();
        if (definitions) {
          definitions.name = newName;
        }
        const elementRegistry = instance.get('elementRegistry');
        elementRegistry.forEach((element) => {
          const bo = element.businessObject;
          if (bo?.['$instanceOf']?.('bpmn:Process')) {
            bo.name = newName;
          }
        });
      } catch (error_) {
        console.warn('Could not update process name for Save As:', error_);
      }
    }

    if (setProcessName) setProcessName(newName);
    currentSharePointPathRef.current = normalizeSharePointPath(`Kundenprojekte/${newName}.bpmn`);
    currentSharePointEtagRef.current = '';
    // Force new file picker by clearing cached handle
    if (directoryHandleRef) directoryHandleRef.current = null;
    // Mark dirty so save proceeds
    if (setIsDirty) setIsDirty(true);
    startSaveTargetDialog('saveAs', newName);
  };

  // --- Unsaved-changes-aware Load & New ---

  const executeLoad = async (xmlContent, fileName, sharePointMeta = null, options = {}) => {
    try {
      // Auto-detect Signavio exports and strip Signavio-specific noise
      // (signavio:* elements, empty extensionElements, branding, namespaces).
      // Native bpmn-io diagrams pass through untouched.
      const { xml: importXml, wasSignavio, detection, cleanup } = detectAndClean(xmlContent);
      if (wasSignavio) {
        console.info(
          '[BPMN] Signavio export detected — auto-cleaned before import.',
          { signals: detection.signals, counts: detection.counts, removed: cleanup?.removed },
        );
        if (cleanup?.warnings?.length) {
          for (const w of cleanup.warnings) console.warn('[BPMN] Signavio cleanup:', w);
        }
        setSignavioImportReport({
          removed: cleanup?.removed ?? {},
          warnings: cleanup?.warnings ?? [],
        });
      }

      const newModeler = await createDiagram(importXml);
      if (onModelerReady && newModeler) {
        onModelerReady(newModeler);
      }
      // Capture baseline snapshot after loading
      await updateBaseline(newModeler);
      // Mark dirty if we mutated the imported XML, so the next save persists
      // the cleaned version to disk. A SGX conversion counts as such a
      // mutation too, even though its XML never carried a Signavio namespace.
      const isFreshImport = wasSignavio || !!options.fresh;
      if (isFreshImport && setIsDirty) setIsDirty(true);
      // Suppress the XML-snapshot in custom:versionHistory on the *first*
      // save after a Signavio import — there is no meaningful previous state
      // to roll back to, and the snapshot would otherwise double the file size.
      if (isFreshImport) freshImportRef.current = true;
      // Extract process name from file name
      if (fileName) {
        const name = fileName.replace(/\.(bpmn|xml)$/i, '');
        if (setProcessName) setProcessName(name);
      }
      if (sharePointMeta?.path) {
        currentSharePointPathRef.current = normalizeSharePointPath(sharePointMeta.path);
        currentSharePointEtagRef.current = sharePointMeta.etag || '';
      } else {
        currentSharePointPathRef.current = '';
        currentSharePointEtagRef.current = '';
      }
      // Reset directory handle — user must choose directory on next save
      if (directoryHandleRef) directoryHandleRef.current = null;
    } catch (err) {
      console.error('Error loading diagram:', err);
      alert('Fehler beim Laden: ' + err.message);
    }
  };

  const executeNew = async (name) => {
    try {
      const newModeler = await clearDiagram();
      if (onModelerReady && newModeler) {
        onModelerReady(newModeler);
      }
      // Capture baseline snapshot after new diagram
      await updateBaseline(newModeler);
      // Set process name in definitions
      if (newModeler?.getDefinitions) {
        try {
          const definitions = newModeler.getDefinitions();
          if (definitions) {
            definitions.name = name;
          }
          // Also set name on the first process element
          const elementRegistry = newModeler.get('elementRegistry');
          elementRegistry.forEach((element) => {
            const bo = element.businessObject;
            if (bo?.['$instanceOf']?.('bpmn:Process')) {
              bo.name = name;
            }
          });
        } catch (error_) {
          console.warn('Could not set process name:', error_);
        }
      }
      if (setProcessName) setProcessName(name);
      currentSharePointPathRef.current = normalizeSharePointPath(`Kundenprojekte/${name}.bpmn`);
      currentSharePointEtagRef.current = '';
      // Reset directory handle for new process
      if (directoryHandleRef) directoryHandleRef.current = null;
    } catch (err) {
      console.error('Error creating new diagram:', err);
      alert('Fehler beim Erstellen: ' + err.message);
    }
  };

  const pickFileAndRead = () => {
    return new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.bpmn,.xml,.sgx';
      input.onchange = async (e) => {
        const file = e.target.files[0];
        if (!file) {
          resolve(null);
          return;
        }
        // A Signavio archive is a ZIP — it is not read here but handed on for
        // conversion.
        if (isSgxFileName(file.name)) {
          resolve({ sgxFile: file, fileName: file.name });
          return;
        }
        const content = await file.text();
        resolve({ content, fileName: file.name });
      };
      input.click();
    });
  };

  const handleLoad = async () => {
    setLoadSourceContext('toolbar');
    setShowLoadSourceDialog(true);
  };

  const handleNew = () => {
    if (isDirty) {
      setPendingAction('new');
    } else {
      setShowNewProcessDialog(true);
    }
  };

  const handleNewProcessConfirm = async (name) => {
    setShowNewProcessDialog(false);
    await executeNew(name);
    startSaveTargetDialog('new', name);
  };

  const handleNewProcessCancel = () => {
    setShowNewProcessDialog(false);
  };

  // --- Welcome Dialog handlers ---

  const handleWelcomeNew = async (name) => {
    setShowWelcomeDialog(false);
    await executeNew(name);
    startSaveTargetDialog('new', name);
  };

  const handleWelcomeLoad = async () => {
    setShowWelcomeDialog(false);
    setLoadSourceContext('welcome');
    setShowLoadSourceDialog(true);
  };

  const handleUnsavedDiscard = async () => {
    const action = pendingAction;
    const content = pendingFileContentRef.current;
    const fileName = pendingFileNameRef.current;
    const filePath = pendingFilePathRef.current;
    const fileEtag = pendingFileEtagRef.current;
    const fileFresh = pendingFileFreshRef.current;
    setPendingAction(null);
    pendingFileContentRef.current = null;
    pendingFileNameRef.current = null;
    pendingFilePathRef.current = null;
    pendingFileEtagRef.current = null;
    pendingFileFreshRef.current = false;
    if (setIsDirty) setIsDirty(false);

    if (action === 'load' && content) {
      await executeLoad(content, fileName, { path: filePath, etag: fileEtag }, { fresh: fileFresh });
    } else if (action === 'new') {
      setShowNewProcessDialog(true);
    }
  };

  const handleUnsavedSave = async () => {
    const action = pendingAction;
    const content = pendingFileContentRef.current;
    const fileName = pendingFileNameRef.current;
    const filePath = pendingFilePathRef.current;
    const fileEtag = pendingFileEtagRef.current;
    const fileFresh = pendingFileFreshRef.current;
    setPendingAction(null);
    pendingFileContentRef.current = null;
    pendingFileNameRef.current = null;
    pendingFilePathRef.current = null;
    pendingFileEtagRef.current = null;
    pendingFileFreshRef.current = false;

    // Save first, then proceed
    await handleSave();

    if (action === 'load' && content) {
      await executeLoad(content, fileName, { path: filePath, etag: fileEtag }, { fresh: fileFresh });
    } else if (action === 'new') {
      setShowNewProcessDialog(true);
    }
  };

  const handleUnsavedCancel = () => {
    setPendingAction(null);
    pendingFileContentRef.current = null;
    pendingFileNameRef.current = null;
    pendingFilePathRef.current = null;
    pendingFileEtagRef.current = null;
    pendingFileFreshRef.current = false;
  };

  // ---- PDF Export (View mode) ----
  const handleExportPdf = async () => {
    const instance = getActiveInstance();
    if (!instance) return;

    try {
      const canvas = instance.get('canvas');
      const elementRegistry = instance.get('elementRegistry');
      const originalRoot = canvas.getRootElement();

      // 1. Main diagram SVG
      canvas.setRootElement(originalRoot);
      const { svg: mainSvg } = await instance.saveSVG();

      // 2. Sub-process diagrams
      const subProcessDiagrams = await collectSubProcessDiagrams(canvas, instance, originalRoot);
      canvas.setRootElement(originalRoot);

      // 3. Task documentation
      const taskDocs = collectTaskDocs(elementRegistry);

      // 3b. Bausteine — nach den Task-Dokumenten, weil das Umschalten der
      //     Diagramme die Elementregistrierung neu aufbaut.
      const bausteinDiagramme = await collectBausteinDiagrams(instance);

      // 4. Build PDF
      // compress: jsPDF schreibt Inhaltsströme sonst ungefiltert — sie machen
      // 97 % der Datei aus und schrumpfen unter FlateDecode auf ein Siebtel.
      // floatPrecision 'smart': kürzt Koordinaten ≥ 1 auf fünf Nachkommastellen,
      // lässt Werte < 1 unangetastet — dort liegen die Skalierungsfaktoren, die
      // svg2pdf in die Transformationsmatrizen schreibt.
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4',
        compress: true,
        floatPrecision: 'smart',
      });
      const vInfo = getVersionInfo();
      await addDiagramPage(pdf, await prepareDiagram(mainSvg), 'BPMN Prozessdokumentation',
        `Version ${vInfo.version} | Autor: ${vInfo.author} | Exportiert: ${new Date().toLocaleDateString('de-DE')}`, true);

      for (const sub of subProcessDiagrams) {
        await addDiagramPage(pdf, await prepareDiagram(sub.svg), `Sub-Prozess: ${sub.name}`, null, false);
      }

      for (const baustein of bausteinDiagramme) {
        await addDiagramPage(pdf, await prepareDiagram(baustein.svg), `Baustein: ${baustein.name}`, null, false);
      }

      await addTaskDocPages(pdf, taskDocs);

      // 5. Download
      pdf.save('prozessdokumentation.pdf');
    } catch (err) {
      console.error('PDF export error:', err);
      alert('Fehler beim PDF-Export: ' + err.message);
    }
  };

  const handleUndo = () => {
    const activeModeler = getActiveModeler();
    if (activeModeler) {
      try {
        const commandStack = activeModeler.get('commandStack');
        if (commandStack.canUndo()) {
          commandStack.undo();
        }
      } catch (err) {
        console.error('Error during undo:', err);
      }
    }
  };

  const handleRedo = () => {
    const activeModeler = getActiveModeler();
    if (activeModeler) {
      try {
        const commandStack = activeModeler.get('commandStack');
        if (commandStack.canRedo()) {
          commandStack.redo();
        }
      } catch (err) {
        console.error('Error during redo:', err);
      }
    }
  };

  const handleZoomIn = () => {
    const activeModeler = getActiveModeler();
    if (activeModeler) {
      try {
        const zoomScroll = activeModeler.get('zoomScroll');
        zoomScroll.stepZoom(1);
      } catch (err) {
        console.error('Error during zoom in:', err);
      }
    }
  };

  const handleZoomOut = () => {
    const activeModeler = getActiveModeler();
    if (activeModeler) {
      try {
        const zoomScroll = activeModeler.get('zoomScroll');
        zoomScroll.stepZoom(-1);
      } catch (err) {
        console.error('Error during zoom out:', err);
      }
    }
  };

  const handleZoomReset = () => {
    const activeModeler = getActiveModeler();
    if (activeModeler) {
      try {
        const canvas = activeModeler.get('canvas');
        canvas.zoom('fit-viewport');
      } catch (err) {
        console.error('Error during zoom reset:', err);
      }
    }
  };

  // --- Alignment & Distribution handlers ---

  const handleAlign = (type) => {
    const activeModeler = getActiveModeler();
    if (!activeModeler) return;
    try {
      const selection = activeModeler.get('selection');
      const alignElements = activeModeler.get('alignElements');
      const selected = selection.get();
      if (selected.length >= 2) {
        alignElements.trigger(selected, type);
      }
    } catch (err) {
      console.error('Error during alignment:', err);
    }
  };

  const handleDistribute = (axis) => {
    const activeModeler = getActiveModeler();
    if (!activeModeler) return;
    try {
      const selection = activeModeler.get('selection');
      const distributeElements = activeModeler.get('distributeElements');
      const selected = selection.get();
      if (selected.length >= 3) {
        distributeElements.trigger(selected, axis);
      }
    } catch (err) {
      console.error('Error during distribution:', err);
    }
  };

  // --- Version Rollback ---

  /** Called from VersionModal when user clicks the rollback icon on an older version */
  const handleRollbackRequest = (entry) => {
    const history = getVersionInfo().history;
    // Find the index of this entry in the original (chronological) history
    const targetIdx = history.findIndex((h) => sameEntry(h, entry));
    // Versions after this entry will be deleted
    const toDelete = targetIdx >= 0
      ? history.slice(targetIdx + 1).filter(hasSnapshot).map((h) => h.version)
      : [];
    setRollbackTarget(entry);
    setRollbackVersionsToDelete(toDelete);
    setShowRollbackConfirm(true);
  };

  /** Download current diagram as backup before rollback */
  const handleRollbackBackup = async () => {
    const instance = getActiveInstance();
    if (!instance) return;
    try {
      const { xml } = await instance.saveXML({ format: true });
      const fileName = (processName || 'backup') + '_backup.bpmn';
      if (globalThis.showSaveFilePicker) {
        try {
          await saveViaFileSystem(xml, fileName);
          return;
        } catch (error_) {
          if (error_.name === 'AbortError') return;
        }
      }
      saveViaDownload(xml, fileName);
    } catch (error_) {
      console.error('Rollback backup error:', error_);
    }
  };

  /** Execute the rollback: restore old XML, truncate history, update version */
  const handleRollbackConfirm = async () => {
    setShowRollbackConfirm(false);
    const entry = rollbackTarget;
    const entryXml = readSnapshot(entry);
    if (!entryXml) {
      console.error('Rollback: no XML in target entry', entry);
      return;
    }
    const instance = getActiveInstance();
    if (!instance?.getDefinitions) {
      console.error('Rollback: no active instance');
      return;
    }

    try {
      const definitions = instance.getDefinitions();
      if (!definitions) return;

      // 1. Read imageStore and rehydrate the snapshot
      let imageStore = {};
      try { imageStore = JSON.parse(readCustomAttr(definitions, 'imageStore') || '{}'); }
      catch { /* invalid JSON */ }
      const rehydratedXml = entry.images
        ? rehydrateSnapshotImages(entryXml, entry.images, imageStore)
        : entryXml;

      // 2. Truncate history: keep only entries up to and including the target
      const parsedHistory = parseHistory(readCustomAttr(definitions, 'versionHistory'));
      if (parsedHistory.corrupt) {
        console.error('Versionshistorie ist unlesbar — Rücksprung abgebrochen.');
        alert('Die Versionshistorie dieser Datei ist beschädigt. Der Rücksprung wurde abgebrochen.');
        return;
      }
      const history = parsedHistory.history;
      const targetIdx = history.findIndex((h) => sameEntry(h, entry));
      // Wie in den Speicherpfaden: erst kappen, dann aufräumen — sonst gelten
      // die Bilder gerade entfernter Stände noch als belegt.
      const truncatedHistory = normalizeHistory(
        targetIdx >= 0 ? history.slice(0, targetIdx) : history,
      );

      // 3. Import the restored XML
      console.log('Rollback: importing XML, length =', rehydratedXml.length);
      const importResult = await instance.importXML(rehydratedXml);
      console.log('Rollback: importXML result =', importResult);

      // 4. Update definitions with truncated history and rolled-back version
      const newDefs = instance.getDefinitions();
      if (newDefs) {
        // Garbage-collect imageStore with truncated history
        let currentHashes = [];
        try { currentHashes = await collectCurrentImageHashes(instance, imageStore); }
        catch { /* */ }
        const cleanedStore = garbageCollectImageStore(imageStore, currentHashes, truncatedHistory);

        persistStoreAndHistory(newDefs, cleanedStore, truncatedHistory);
        newDefs.set('custom:version', entry.version);
        newDefs.set('custom:author', entry.author || authorName || 'Unbekannt');
        newDefs.set('custom:lastModified', new Date().toISOString().split('T')[0]);
        setVersionDisplay(entry.version);
      }

      // 5. Fit viewport and clear command stack
      instance.get('canvas').zoom('fit-viewport');
      try { instance.get('commandStack').clear(); }
      catch { /* */ }
      if (setIsDirty) setIsDirty(true);

      // 6. Update baseline to restored state
      await updateBaseline(instance);
    } catch (error_) {
      console.error('Rollback error:', error_);
      alert('Fehler beim Rollback: ' + error_.message);
    }
    setRollbackTarget(null);
    setRollbackVersionsToDelete([]);
  };

  return (
    <div className="toolbar">
      {!isViewMode && (
        <div className="toolbar-group">
          <button className="btn btn-primary btn-sm" onClick={handleNew} title="Neuer Prozess">
            <i className="bi bi-file-earmark-plus"></i>
          </button>
          <button className="btn btn-secondary btn-sm" onClick={handleLoad} title="Prozess laden">
            <i className="bi bi-folder2-open"></i>
          </button>
          <div className="save-split-btn" ref={saveDropdownRef}>
            <button className="btn btn-success btn-sm btn-save-main" onClick={handleSave} title="Prozess speichern" disabled={!processName || !isDirty}>
              <i className="bi bi-save"></i> Speichern
            </button>
            <button
              className="btn btn-success btn-sm btn-save-caret"
              onClick={() => setShowSaveDropdown((v) => !v)}
              title="Speicheroptionen"
              disabled={!processName}
            >
              <i className="bi bi-chevron-down"></i>
            </button>
            {showSaveDropdown && (
              <div className="save-dropdown">
                <button onClick={() => { setShowSaveDropdown(false); setShowSaveAsDialog(true); }} title="Speichern unter…">
                  <i className="bi bi-save2"></i> Speichern unter…
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      {isViewMode && (
        <div className="toolbar-group">
          <button className="btn btn-outline-info btn-sm" onClick={handleExportPdf} title="Als PDF exportieren">
            <i className="bi bi-file-earmark-pdf"></i>
          </button>
        </div>
      )}
      {!isViewMode && (
        <div className="toolbar-group">
          <button className="btn btn-outline-primary btn-sm" onClick={handleUndo} title="Undo">
            <i className="bi bi-arrow-counterclockwise"></i>
          </button>
          <button className="btn btn-outline-primary btn-sm" onClick={handleRedo} title="Redo">
            <i className="bi bi-arrow-clockwise"></i>
          </button>
        </div>
      )}
      {!isViewMode && (
        <div className="toolbar-group">
          <div className="align-split-btn" ref={alignDropdownRef}>
            <button
              className="btn btn-outline-primary btn-sm"
              onClick={() => setShowAlignDropdown((v) => !v)}
              title="Ausrichten & Verteilen"
              disabled={selectedCount < 2}
            >
              <i className="bi bi-align-center"></i>
            </button>
            {showAlignDropdown && (
              <div className="align-dropdown">
                <div className="align-dropdown-section">
                  <span className="align-dropdown-label">Ausrichten</span>
                  <div className="align-dropdown-row">
                    <button onClick={() => handleAlign('left')} title="Links ausrichten" disabled={selectedCount < 2}>
                      <i className="bi bi-align-start"></i>
                    </button>
                    <button onClick={() => handleAlign('center')} title="Horizontal zentrieren" disabled={selectedCount < 2}>
                      <i className="bi bi-align-center"></i>
                    </button>
                    <button onClick={() => handleAlign('right')} title="Rechts ausrichten" disabled={selectedCount < 2}>
                      <i className="bi bi-align-end"></i>
                    </button>
                  </div>
                  <div className="align-dropdown-row">
                    <button onClick={() => handleAlign('top')} title="Oben ausrichten" disabled={selectedCount < 2}>
                      <i className="bi bi-align-top"></i>
                    </button>
                    <button onClick={() => handleAlign('middle')} title="Vertikal zentrieren" disabled={selectedCount < 2}>
                      <i className="bi bi-align-middle"></i>
                    </button>
                    <button onClick={() => handleAlign('bottom')} title="Unten ausrichten" disabled={selectedCount < 2}>
                      <i className="bi bi-align-bottom"></i>
                    </button>
                  </div>
                </div>
                <div className="align-dropdown-divider"></div>
                <div className="align-dropdown-section">
                  <span className="align-dropdown-label">Verteilen</span>
                  <div className="align-dropdown-row">
                    <button onClick={() => handleDistribute('horizontal')} title="Horizontal verteilen" disabled={selectedCount < 3}>
                      <i className="bi bi-distribute-horizontal"></i>
                    </button>
                    <button onClick={() => handleDistribute('vertical')} title="Vertikal verteilen" disabled={selectedCount < 3}>
                      <i className="bi bi-distribute-vertical"></i>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      <div className="toolbar-group">
        <button className="btn btn-outline-secondary btn-sm" onClick={handleZoomIn} title="Zoom In">
          <i className="bi bi-zoom-in"></i>
        </button>
        <button className="btn btn-outline-secondary btn-sm" onClick={handleZoomOut} title="Zoom Out">
          <i className="bi bi-zoom-out"></i>
        </button>
        <button className="btn btn-outline-secondary btn-sm" onClick={handleZoomReset} title="Fit to Screen">
          <i className="bi bi-arrows-angle-contract"></i>
        </button>
        <button
          className={`btn btn-sm ${gridVisible ? 'btn-secondary' : 'btn-outline-secondary'}`}
          onClick={onToggleGrid}
          title={gridVisible ? 'Raster ausblenden' : 'Raster anzeigen'}
        >
          <i className="bi bi-grid-3x3"></i>
        </button>
      </div>
      <div className="toolbar-separator"></div>
      <div className="toolbar-group">
        <button
          className={`btn btn-sm mode-toggle-btn ${isViewMode ? 'btn-info' : 'btn-warning'}`}
          onClick={onModeChange}
          title={isViewMode ? 'Zum Bearbeitungsmodus wechseln' : 'Zum Ansichtsmodus wechseln'}
        >
          <i className={`bi ${isViewMode ? 'bi-pencil' : 'bi-eye'}`}></i>
        </button>
        <span className={`mode-badge ${isViewMode ? 'mode-badge-view' : 'mode-badge-edit'}`}>
          {isViewMode ? 'VIEW' : 'EDIT'}
        </span>
      </div>
      <div className="toolbar-separator"></div>
      <div className="toolbar-group">
        {(() => {
          let videoButtonClass, videoButtonTitle;
          if (videoPlayerOpen) {
            videoButtonClass = 'btn-danger';
            videoButtonTitle = 'Video-Player schließen';
          } else if (hasVideoUrl) {
            videoButtonClass = 'btn-success';
            videoButtonTitle = 'Video-Player öffnen (Video zugeordnet)';
          } else {
            videoButtonClass = 'btn-outline-light';
            videoButtonTitle = 'Video-Player öffnen';
          }
          return (
            <button
              className={`btn btn-sm ${videoButtonClass}`}
              onClick={onToggleVideo}
              title={videoButtonTitle}
            >
              <i className="bi bi-camera-video"></i>
              {videoPlayerOpen ? ' ✕' : ''}
            </button>
          );
        })()}
      </div>
      <div className="toolbar-separator"></div>
      <div className="toolbar-group">
        <button
          className="btn btn-outline-light btn-sm"
          onClick={handleOpenVersionModal}
          title="Versionsverwaltung"
        >
          <i className="bi bi-tag"></i>
        </button>
        <span className="version-badge">v{versionDisplay}</span>
      </div>
      {processName && (
        <div className="toolbar-group">
          <span className="process-name-badge" title={processName}>
            <i className="bi bi-diagram-3"></i> {processName}
          </span>
        </div>
      )}
      <div className="toolbar-spacer"></div>
      <div className="toolbar-group">
        <button
          className={`btn btn-sm ${helpPanelOpen ? 'btn-info' : 'btn-outline-light'}`}
          onClick={onToggleHelp}
          title="Benutzerhandbuch"
        >
          <i className="bi bi-question-circle"></i>
        </button>
      </div>
      <div className="toolbar-group toolbar-user">
        <span className="user-badge" title={authorName}>
          <i className="bi bi-person-circle"></i> {authorName}
        </span>
        {isAuthenticated && onLogout && (
          <button onClick={onLogout} className="btn btn-outline-light btn-sm btn-logout" title="Abmelden" aria-label="Abmelden">
            <i className="bi bi-box-arrow-right"></i>
          </button>
        )}
      </div>

      {showLoadSourceDialog && (
        <div className="sp-load-overlay" role="dialog" aria-modal="true" aria-label="Ladequelle auswählen">
          <div className="sp-load-modal">
            <h5>Datei laden</h5>
            <p>Bitte Quelle auswählen (.bpmn oder Signavio-Archiv .sgx):</p>
            <div className="sp-load-actions">
              <button className="btn btn-primary btn-sm" onClick={handleLoadSourceSharePoint}>
                <i className="bi bi-cloud-arrow-down"></i> Aus SharePoint
              </button>
              <button className="btn btn-outline-light btn-sm" onClick={handleLoadSourceLocal}>
                <i className="bi bi-folder2-open"></i> Lokale Datei
              </button>
            </div>
            <div className="sp-load-actions">
              <button className="btn btn-secondary btn-sm" onClick={handleLoadSourceCancel}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}

      {sgxBusy && (
        <div className="sp-load-overlay" role="dialog" aria-modal="true" aria-label="Signavio-Archiv wird verarbeitet">
          <div className="sp-load-modal">
            <h5>Signavio-Archiv</h5>
            <p>{sgxBusy}</p>
            <p className="sgx-hint">
              Große Archive enthalten mehrere hundert Prozesse — das kann eine Minute dauern.
            </p>
          </div>
        </div>
      )}

      {!sgxBusy && sgxError && !showSgxPicker && (
        <div className="sp-load-overlay" role="dialog" aria-modal="true" aria-label="SGX-Import fehlgeschlagen">
          <div className="sp-load-modal">
            <h5>SGX-Import fehlgeschlagen</h5>
            <div className="sp-error">{sgxError}</div>
            <div className="sp-load-actions">
              <button className="btn btn-secondary btn-sm" onClick={handleSgxErrorDismiss}>Schließen</button>
            </div>
          </div>
        </div>
      )}

      {showSgxPicker && sgxImport && (
        <div className="sp-load-overlay" role="dialog" aria-modal="true" aria-label="Prozess aus Signavio-Archiv wählen">
          <div className="sp-load-modal sp-load-modal-wide">
            <h5>Prozess aus „{sgxImport.sourceName}“ wählen</h5>
            <p>
              Das Archiv enthält {sgxImport.diagrams.length} Prozesse. Der gewählte Prozess
              wird als neues, noch ungespeichertes Dokument geöffnet.
            </p>

            <div className="sgx-search-row">
              <input
                id="sgx-filter"
                type="text"
                value={sgxFilter}
                onChange={(e) => setSgxFilter(e.target.value)}
                placeholder="Nach Kunde oder Prozess filtern…"
                aria-label="Prozesse filtern"
                autoFocus
              />
              <span className="sp-file-meta">{displayedSgxDiagrams.length} Treffer</span>
            </div>

            {sgxError && <div className="sp-error">{sgxError}</div>}

            <div className="sp-file-list">
              {displayedSgxDiagrams.length === 0 && (
                <div className="sp-empty">Kein Prozess passt zum Filter.</div>
              )}
              {displayedSgxDiagrams.map((entry) => (
                <button
                  key={entry.id}
                  type="button"
                  className="sp-file-item"
                  onClick={() => handleSgxPick(entry)}
                >
                  <span className="sp-file-name">📄 {entry.name}</span>
                  <span className="sp-file-path">{entry.customer}</span>
                  <span className="sp-file-meta">{Math.max(1, Math.round((entry.bytes || 0) / 1024))} KB</span>
                </button>
              ))}
            </div>

            <div className="sp-load-actions">
              <button className="btn btn-secondary btn-sm" onClick={handleSgxPickerCancel}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}

      {showSaveTargetDialog && (
        <div className="sp-load-overlay" role="dialog" aria-modal="true" aria-label="Speicherziel auswählen">
          <div className="sp-load-modal">
            <h5>{saveTargetContext === 'new' ? 'Neues Dokument speichern' : 'Speichern unter'}</h5>
            <p>Bitte Speicherziel auswählen:</p>
            <div className="sp-load-actions">
              <button className="btn btn-primary btn-sm" onClick={handleSaveTargetSharePoint}>
                <i className="bi bi-cloud-arrow-up"></i> In SharePoint
              </button>
              <button className="btn btn-outline-light btn-sm" onClick={handleSaveTargetLocal}>
                <i className="bi bi-hdd"></i> Lokal speichern
              </button>
            </div>
            <div className="sp-load-actions">
              <button className="btn btn-secondary btn-sm" onClick={handleSaveTargetCancel}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}

      {showSharePointDialog && (
        <div className="sp-load-overlay" role="dialog" aria-modal="true" aria-label={sharePointDialogMode === 'save' ? 'SharePoint-Datei speichern' : 'SharePoint-Datei laden'}>
          <div className="sp-load-modal sp-load-modal-wide">
            <h5>{sharePointDialogMode === 'save' ? 'In SharePoint speichern' : 'SharePoint-Datei laden'}</h5>

            <div className="sp-browser-row">
              <button
                type="button"
                className="btn btn-outline-light btn-sm"
                onClick={handleSharePointNavigateUp}
                disabled={sharePointLoading || /^kundenprojekte$/i.test(normalizeSharePointPath(sharePointBrowsePath))}
                title="Eine Ebene nach oben"
              >
                <i className="bi bi-arrow-up"></i>
              </button>
              <div className="sp-breadcrumbs" aria-label="SharePoint-Pfad">
                {sharePointBreadcrumbs.map((crumb, idx) => {
                  const isLast = idx === sharePointBreadcrumbs.length - 1;
                  return (
                    <React.Fragment key={crumb.path}>
                      <button
                        type="button"
                        className={`sp-breadcrumb ${isLast ? 'is-active' : ''}`}
                        onClick={() => handleSharePointNavigateTo(crumb.path)}
                        disabled={sharePointLoading || isLast}
                        title={crumb.path}
                      >
                        {crumb.label}
                      </button>
                      {!isLast && <span className="sp-breadcrumb-sep">/</span>}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>

            <div className="sp-reload-row">
              <button
                type="button"
                className="btn btn-outline-light btn-sm"
                onClick={() => refreshSharePointFiles(sharePointBrowsePath)}
                disabled={sharePointLoading}
                title="Ordner neu laden"
              >
                <i className="bi bi-arrow-clockwise"></i> Neu laden
              </button>
            </div>

            {sharePointDialogMode === 'load' && sharePointRecentFiles.length > 0 && (
              <div className="sp-recent-block">
                <div className="sp-recent-header">
                  <span>Zuletzt genutzt</span>
                  {sharePointRecentFiles.length > 5 && (
                    <button
                      type="button"
                      className="btn btn-outline-light btn-sm"
                      onClick={() => setSharePointShowAllRecent((prev) => !prev)}
                      disabled={sharePointLoading}
                    >
                      {sharePointShowAllRecent ? 'Weniger' : '...'}
                    </button>
                  )}
                </div>
                <div className="sp-recent-list">
                  {displayedRecentFiles.map((file) => (
                    <button
                      key={`recent-${file.path || file.name}`}
                      type="button"
                      className="sp-recent-item"
                      onClick={() => handleSharePointRecentFilePick(file)}
                      disabled={sharePointLoading}
                      title={file.path || file.name}
                    >
                      <span className="sp-recent-name">{file.name}</span>
                      <span className="sp-recent-path">{file.path}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {sharePointError && <div className="sp-error">{sharePointError}</div>}

            <div className="sp-file-list" aria-busy={sharePointLoading ? 'true' : 'false'}>
              {sharePointLoading && <div className="sp-loading">Lade SharePoint-Dateien…</div>}
              {!sharePointLoading && displayedSharePointFiles.length === 0 && (
                <div className="sp-empty">Keine passenden Dateien oder Ordner gefunden.</div>
              )}
              {!sharePointLoading && displayedSharePointFiles.map((file) => (
                <button
                  key={file.id || file.path || file.name}
                  type="button"
                  className="sp-file-item"
                  onClick={() => handleSharePointFilePick(file)}
                >
                  <span className="sp-file-name">
                    {(() => {
                      if (file.isFolder) return '📁 ';
                      return /\.sgx$/i.test(String(file.name || '')) ? '🗜️ ' : '📄 ';
                    })()}{file.name}
                  </span>
                  <span className="sp-file-path">{file.path || 'Kundenprojekte'}</span>
                  <span className="sp-file-meta">{formatSharePointMeta(file)}</span>
                </button>
              ))}
            </div>

            {sharePointDialogMode === 'save' && (
              <div className="sp-save-name-row">
                <label htmlFor="sp-save-name">Dateiname</label>
                <input
                  id="sp-save-name"
                  type="text"
                  value={sharePointSaveFileName}
                  onChange={(e) => setSharePointSaveFileName(e.target.value)}
                  placeholder="prozess.bpmn"
                  autoFocus
                />
              </div>
            )}

            <div className="sp-load-actions">
              {sharePointDialogMode === 'save' && (
                <button className="btn btn-primary btn-sm" onClick={handleSharePointSaveConfirm} disabled={sharePointLoading}>
                  <i className="bi bi-save"></i> Hier speichern
                </button>
              )}
              <button className="btn btn-secondary btn-sm" onClick={handleSharePointDialogCancel}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}

      {showVersionModal && (
        <VersionModal
          isOpen={showVersionModal}
          onClose={() => setShowVersionModal(false)}
          currentVersion={getVersionInfo().version}
          currentAuthor={getVersionInfo().author}
          loggedInAuthor={authorName}
          versionHistory={getVersionInfo().history}
          onNewVersion={handleNewVersion}
          onViewVersion={onViewVersion}
          onRollback={handleRollbackRequest}
          readOnly={isViewMode}
        />
      )}
      <UnsavedChangesDialog
        isOpen={pendingAction !== null}
        onDiscard={handleUnsavedDiscard}
        onSave={handleUnsavedSave}
        onCancel={handleUnsavedCancel}
      />
      <NewProcessDialog
        isOpen={showSaveAsDialog}
        onConfirm={handleSaveAs}
        onCancel={() => setShowSaveAsDialog(false)}
        title="Speichern unter"
        icon="bi bi-save2"
        submitLabel="Speichern"
        submitIcon="bi bi-save"
        defaultName={processName || ''}
      />
      <NewProcessDialog
        isOpen={showNewProcessDialog}
        onConfirm={handleNewProcessConfirm}
        onCancel={handleNewProcessCancel}
      />
      <WelcomeDialog
        show={showWelcomeDialog}
        authorName={authorName}
        onNewDiagram={handleWelcomeNew}
        onLoadDiagram={handleWelcomeLoad}
      />
      <SaveSuccessDialog
        show={showSaveSuccess}
        onClose={() => setShowSaveSuccess(false)}
      />

      {signavioImportReport && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed', bottom: '1.5rem', left: '50%', transform: 'translateX(-50%)',
            zIndex: 9000, background: '#1e3a5f', color: '#fff',
            borderRadius: '8px', padding: '0.75rem 1.25rem', maxWidth: '520px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.3)', fontSize: '0.875rem', lineHeight: 1.5,
          }}
        >
          <strong>Signavio-Import bereinigt</strong>
          {signavioImportReport.removed.itSystemConversions > 0 && (
            <span>
              {' · '}
              {signavioImportReport.removed.itSystemConversions} IT-System-Symbol
              {signavioImportReport.removed.itSystemConversions !== 1 ? 'e' : ''} als Computer-Icon dargestellt
            </span>
          )}
          {signavioImportReport.removed.signavioElements > 0 && (
            <span> · {signavioImportReport.removed.signavioElements} Signavio-Elemente entfernt</span>
          )}
          {signavioImportReport.warnings.map((w, i) => (
            <div key={i} style={{ color: '#ffd580', marginTop: '0.25rem' }}>⚠ {w}</div>
          ))}
          <button
            type="button"
            onClick={() => setSignavioImportReport(null)}
            style={{
              marginLeft: '1rem', background: 'transparent', border: '1px solid rgba(255,255,255,0.5)',
              borderRadius: '4px', color: '#fff', padding: '0.1rem 0.5rem', cursor: 'pointer',
              fontSize: '0.8rem',
            }}
          >
            ✕
          </button>
        </div>
      )}

      <RollbackConfirmDialog
        isOpen={showRollbackConfirm}
        targetVersion={rollbackTarget?.version || ''}
        versionsToDelete={rollbackVersionsToDelete}
        onConfirm={handleRollbackConfirm}
        onCancel={() => { setShowRollbackConfirm(false); setRollbackTarget(null); }}
        onBackupDownload={handleRollbackBackup}
      />
    </div>
  );
};

Toolbar.propTypes = {
  modeler: PropTypes.object,
  mode: PropTypes.string.isRequired,
  onModeChange: PropTypes.func.isRequired,
  onToggleVideo: PropTypes.func.isRequired,
  videoPlayerOpen: PropTypes.bool,
  hasVideoUrl: PropTypes.bool,
  onModelerReady: PropTypes.func,
  isDirty: PropTypes.bool,
  setIsDirty: PropTypes.func,
  processName: PropTypes.string,
  setProcessName: PropTypes.func,
  directoryHandleRef: PropTypes.shape({ current: PropTypes.any }),
  authorName: PropTypes.string,
  isAuthenticated: PropTypes.bool,
  onLogout: PropTypes.func,
  helpPanelOpen: PropTypes.bool,
  onToggleHelp: PropTypes.func,
  gridVisible: PropTypes.bool,
  onToggleGrid: PropTypes.func,
  viewingVersion: PropTypes.string,
  onViewVersion: PropTypes.func,
  onExitVersionView: PropTypes.func,
};

export default Toolbar;